import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfTableComponent from '../PdfComponents/PdfTable';
import { documentTypesEnum } from '../../../api/vouchers/helpers';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';

const WithdrawalPdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);
    const {
        info: {
            remark,
            dueDate,
            travelFileNumber,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                passenger,
                agentInfo,
                supplierInfo,
                voucherCertification = 'Draft',
                bottomDetails = "",
                columns,
                rows,
                summaryLine,
                usesValueDate = true, // Withdrawals use "Value date:" instead of "Due Date:"
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl
            }
        },
        number: voucherNumber,
        createdAt
    } = props;

    return (
        <Document title={`Withdrawal ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {passenger ? <PdfPassengerDetails passenger={passenger} /> : <></>}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}
                                {supplierInfo ? <PdfBillingContactDetails {...supplierInfo} /> : <></>}
                            </View>

                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                usesValueDate={usesValueDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={[styles.button]}>{voucherCertification}</Text>
                    </View>

                    <View style={[styles.section, styles.alignCenter]} wrap={false}>
                        <PdfMainDetails
                            topDetails={`${documentTypesEnum.WITHDRAWAL} No. ${
                                voucherNumber || 'No Document Number'
                            }`}
                            bottomDetails={travelFileNumber ? `Travel File No. ${travelFileNumber}` : bottomDetails}
                            withForLabel={!travelFileNumber && bottomDetails?.length}
                            mainDetailsTopStyle={styles.padding3_0}
                            mainDetailsBottomStyle={styles.padding2_0}
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent
                            columns={columns}
                            rows={rows}
                            summaryLine={summaryLine}
                            summaryLineStyle={styles.fontBoldAndBig1}
                            headerLineStyle={styles.fontBoldAndBig2}
                        />
                    </View>

                    <PdfRemarksSection body={remark} />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(WithdrawalPdf);
